import { useEnvironmentInterface } from "environment-interface";
import type { CsbApi } from "environment-interface/csbApi";
import { useGitHubReposQuery } from "queries/import";
import { useMemo } from "react";
import useSWR from "swr";

import type { UseGithubRepo, UseValidateProjectName } from "./types";

/**
 * Re-exported hooks used in the Import component for convenience
 */
export { useForkProject } from "hooks/api/useForkProject";

export { useAddStatusNotification } from "features/notifications/hooks";

/**
 * Repo
 */
const buildAccessLimitedError = (owner: string) =>
  `Although you appear to have the correct authorization credentials, the \`${owner}\` organization has enabled OAuth App access restrictions, meaning that data access to third-parties is limited. For more information on these restrictions, including how to enable this app, visit https://docs.github.com/articles/restricting-access-to-your-organization-s-data/`;

/**
 * Given an owner and repo name, fetch data from the GitHub API
 *
 * @param owner
 * @param repo
 * @param shouldFetch
 * @returns
 */
export const useGithubRepo: UseGithubRepo = (owner, repo, shouldFetch) => {
  const { data, error } = useGitHubReposQuery(
    shouldFetch && owner && repo ? { owner, repo } : undefined,
  );

  const memoError = useMemo<
    { message: string; type: "ACCESS_LIMITED" | "DEFAULT" } | undefined
  >(() => {
    if (!error) {
      return undefined;
    }

    /**
     * Ugly hack to check if the error cause is not
     * having OAuth access to the owner's repos.
     */
    const isLimitedAccessError = Boolean(
      owner && error.message === buildAccessLimitedError(owner),
    );

    return {
      message: error.message,
      type: isLimitedAccessError ? "ACCESS_LIMITED" : "DEFAULT",
    };
  }, [error, owner]);

  return {
    data: data?.githubRepo,
    error: memoError,
  };
};

/**
 * Final Submit
 */

const MESSAGES = {
  tooShort: "Name cannot be empty.",
};

type ValidationResponse = { valid: boolean; message?: string };

export const useValidateProjectName: UseValidateProjectName = (
  destinationOwnerName,
  destinationName,
) => {
  const { csbApi } = useEnvironmentInterface();

  const validateName = (fullName: string) =>
    (csbApi as CsbApi).rest.get<ValidationResponse>({
      path: `/beta/repos/validate/github/${fullName}`,
    });

  const { data, error: requestError } = useSWR<ValidationResponse, Error>(
    destinationName !== ""
      ? `${destinationOwnerName}/${destinationName}`
      : undefined,
    validateName,
  );

  if (destinationName.includes(" ")) {
    return { status: "error", error: "Name cannot contain spaces." };
  }

  const tooShortError = destinationName === "" && MESSAGES.tooShort;
  const error = requestError?.message || data?.message || tooShortError;

  if (data?.valid) return { status: "valid" };
  if (error) return { status: "error", error };

  return { status: "validating" };
};
